import moment from "moment-timezone";

import { Store } from "~/bff/types/Store";
import { StoreHours } from "~/bff/types/StoreHours";
import { StoreOpenIntervals } from "~/bff/types/StoreOpenIntervals";

import { getDayFromDate } from "../get-day-from-date";
import { isOpen } from "../is-open";

const hasOpenHours = (hours: (StoreOpenIntervals | null)[]): boolean => {
  if (!hours.length) {
    return false;
  }

  return hours.some(
    (interval) => interval !== null && interval?.openIntervals?.length,
  );
};

const getOpenDateFromHours = (
  storeOpenIntervals: StoreOpenIntervals | undefined,
  timezone?: string,
) => {
  const { date, openIntervals } = storeOpenIntervals ?? {};

  const resultDate = moment(date, "YYYY-M-D");

  resultDate.hours(+(openIntervals?.[0]?.start?.split?.(":")?.[0] ?? 0));
  resultDate.minutes(+(openIntervals?.[0]?.start?.split?.(":")?.[1] ?? 0));

  // keepLocalTime parameter is used as we have to return date in store original timezone
  // (hours provided for store/service are based on it)
  return timezone ? resultDate.tz(timezone, true) : resultDate;
};

const getCloseDateFromHours = (
  storeOpenIntervals: StoreOpenIntervals | undefined,
  timezone?: string,
) => {
  const { date, openIntervals } = storeOpenIntervals ?? {};

  const resultDate = moment(date, "YYYY-M-D");

  resultDate.hours(+(openIntervals?.[0]?.end?.split?.(":")?.[0] ?? 0));
  resultDate.minutes(+(openIntervals?.[0]?.end?.split?.(":")?.[1] ?? 0));

  // keepLocalTime parameter is used as we have to return date in store original timezone
  // (hours provided for store/service are based on it)
  return timezone ? resultDate.tz(timezone, true) : resultDate;
};

const getCombinedHoursForDate = ({
  regularHours,
  holidayHours,
  date: providedDate,
}: {
  regularHours?: StoreHours;
  holidayHours?: Store["holidayHours"];
  date: moment.Moment;
}): StoreOpenIntervals => {
  const holidayHoursForDate = holidayHours?.find(({ date: holidayDate }) => {
    return moment(holidayDate).isSame(providedDate, "day");
  });

  if (holidayHoursForDate) {
    return holidayHoursForDate;
  }

  const dateDay = getDayFromDate(providedDate);

  // 'y-M-D' formatting is done for compatibility with "yext" data format
  return {
    ...(dateDay ? regularHours?.[dateDay] : {}),
    date: providedDate.format("y-M-D"),
  };
};

const getClosestOpenHours = ({
  regularHours,
  holidayHours,
  timezone,
  startDate,
}: {
  regularHours: StoreHours;
  holidayHours: Store["holidayHours"];
  timezone: string | undefined;
  startDate: moment.Moment;
}): StoreOpenIntervals | null => {
  const currentDate = timezone ? moment(startDate).tz(timezone) : moment(startDate);

  const LIMIT_DAYS_TO_FIND_CLOSEST_OPENING_HOURS = 100;
  for (let i = 0; i < LIMIT_DAYS_TO_FIND_CLOSEST_OPENING_HOURS; i++) {
    const currentHours = getCombinedHoursForDate({
      regularHours,
      holidayHours,
      date: currentDate,
    });

    if (currentHours?.isClosed) {
      currentDate.add(1, "days");
      continue;
    }

    const openDateNoTz = getOpenDateFromHours(currentHours, timezone);

    const openDate = timezone
      ? getOpenDateFromHours(currentHours, timezone).tz(timezone, true)
      : openDateNoTz;

    if (openDate.isAfter(startDate)) {
      return currentHours;
    }

    currentDate.add(1, "days");
  }

  return null;
};

export const getStoreOrServiceStatus = ({
  regularHours,
  holidayHours,
  timezone,
}: {
  regularHours?: Store["hours"];
  holidayHours?: Store["holidayHours"];
  timezone?: string;
}):
  | { status: "open"; openDate: moment.Moment; closeDate: moment.Moment }
  | {
      status: "closed";
      openDate: moment.Moment | null;
      closeDate: moment.Moment | null;
    } => {
  if (!regularHours || !hasOpenHours(Object.values(regularHours))) {
    return {
      status: "closed",
      openDate: null,
      closeDate: null,
    };
  }

  const now = timezone ? moment().tz(timezone) : moment();

  const currentDayHours = getCombinedHoursForDate({
    regularHours,
    holidayHours,
    date: now,
  });

  const isOpenNow = isOpen(now, currentDayHours);

  if (isOpenNow) {
    return {
      status: "open",
      openDate: getOpenDateFromHours(currentDayHours, timezone),
      closeDate: getCloseDateFromHours(currentDayHours, timezone),
    };
  }

  const closestOpenHours = getClosestOpenHours({
    regularHours,
    holidayHours,
    timezone,
    startDate: now,
  });

  return {
    status: "closed",
    openDate: getOpenDateFromHours(closestOpenHours ?? undefined, timezone),
    closeDate: getCloseDateFromHours(closestOpenHours ?? undefined, timezone),
  };
};
